import React, { useState } from "react";
import moment from "moment";
import MatrixCell from "./MatrixCell";

const IDEAL_TRAINERS = 4;
const IDEAL_RECEPTIONISTS = 2;

const today = () => moment().format("YYYY-MM-DD");

const getSubCellColour = (count, ideal, hasData) => {
  if (!hasData) return "neutral";
  if (count === 0) return "red";
  if (count >= ideal) return "green";
  return "yellow";
};

/**
 * Heatmap grid — gyms as rows, dates as columns.
 *
 * Props:
 *   gymDateMap: { [gymId]: { gym_name, dates: { [date]: { trainers, receptionists } } } }
 *   windowDates: string[]  — 7 (or fewer) "YYYY-MM-DD" strings
 *   activeCell: { gymId, date } | null
 *   onCellClick: (gymId, date, staffList, gymName) => void
 *   loading: boolean
 */
const MatrixGrid = ({ gymDateMap, windowDates, activeCell, onCellClick, loading }) => {
  const [expandedGyms, setExpandedGyms] = useState(new Set());
  const todayStr = today();

  const toggleGym = (gymId) => {
    setExpandedGyms((prev) => {
      const next = new Set(prev);
      if (next.has(gymId)) next.delete(gymId);
      else next.add(gymId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="sdm-grid-wrap">
        <table className="sdm-grid">
          <thead>
            <tr>
              <th className="sdm-grid__gym-col">Gym</th>
              {windowDates.map((d) => (
                <th key={d}>{moment(d).format("ddd D")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(8)].map((_, i) => (
              <tr key={i} className="sdm-skeleton-row">
                <td className="sdm-grid__gym-col">&nbsp;</td>
                {windowDates.map((d) => <td key={d}>&nbsp;</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const gymEntries = Object.entries(gymDateMap);
  if (gymEntries.length === 0) return null;

  return (
    <div className="sdm-grid-wrap">
      <table className="sdm-grid">
        <thead>
          <tr>
            <th className="sdm-grid__gym-col">Gym</th>
            {windowDates.map((d) => (
              <th key={d} style={d === todayStr ? { color: "#1e40af" } : {}}>
                {moment(d).format("ddd D")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gymEntries.map(([gymId, { gym_name, dates }]) => {
            const isExpanded = expandedGyms.has(gymId);

            // Compute deficit days across the window for each role
            let trainerDeficitDays = 0;
            let fdDeficitDays = 0;
            windowDates.forEach((date) => {
              if (date > todayStr) return;
              const day = dates[date];
              if (!day) return;
              const tPresent = (day.trainers || []).filter((s) => s.isPresent).length;
              const rPresent = (day.receptionists || []).filter((s) => s.isPresent).length;
              if (tPresent < IDEAL_TRAINERS) trainerDeficitDays++;
              if (rPresent < IDEAL_RECEPTIONISTS) fdDeficitDays++;
            });

            return (
              <React.Fragment key={gymId}>
                {/* Main gym row */}
                <tr>
                  <td className="sdm-grid__gym-col" title={gym_name}>
                    <div className="sdm-gym-row-header">
                      <button
                        type="button"
                        className="sdm-gym-toggle"
                        onClick={() => toggleGym(gymId)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? "▾" : "▸"}
                      </button>
                      <span className="sdm-gym-name">{gym_name}</span>
                    </div>
                  </td>
                  {windowDates.map((date) => {
                    const isFuture = date > todayStr;
                    const dayData = dates[date];
                    const hasData = !isFuture && !!dayData;

                    const presentCount = hasData
                      ? [
                          ...(dayData.trainers || []),
                          ...(dayData.receptionists || []),
                        ].filter((s) => s.isPresent).length
                      : 0;

                    const staffList = hasData
                      ? [...(dayData.trainers || []), ...(dayData.receptionists || [])]
                      : [];

                    const isActive =
                      activeCell?.gymId === gymId && activeCell?.date === date;

                    return (
                      <MatrixCell
                        key={date}
                        present={presentCount}
                        hasData={hasData}
                        isActive={isActive}
                        onClick={hasData ? () => onCellClick(gymId, date, staffList, gym_name) : undefined}
                      />
                    );
                  })}
                </tr>

                {/* Accordion: Trainers sub-row */}
                {isExpanded && (
                  <tr className="sdm-sub-row">
                    <td className="sdm-grid__gym-col">
                      <div className="sdm-sub-row__label-inner">
                        <span>Trainers</span>
                        {trainerDeficitDays > 0 && (
                          <span
                            className="sdm-deficit-badge"
                            title={`Fewer than ${IDEAL_TRAINERS} trainers present on ${trainerDeficitDays} day${trainerDeficitDays !== 1 ? "s" : ""} this week`}
                          >
                            {trainerDeficitDays}d
                          </span>
                        )}
                      </div>
                    </td>
                    {windowDates.map((date) => {
                      const isFuture = date > todayStr;
                      const dayData = dates[date];
                      const hasData = !isFuture && !!dayData;
                      const count = hasData
                        ? (dayData.trainers || []).filter((s) => s.isPresent).length
                        : 0;
                      const colour = getSubCellColour(count, IDEAL_TRAINERS, hasData);
                      return (
                        <td key={date}>
                          <span className={`sdm-sub-cell sdm-sub-cell--${colour}`}>
                            {hasData ? count : ""}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                )}

                {/* Accordion: Front Desk sub-row */}
                {isExpanded && (
                  <tr className="sdm-sub-row">
                    <td className="sdm-grid__gym-col">
                      <div className="sdm-sub-row__label-inner">
                        <span>Front Desk</span>
                        {fdDeficitDays > 0 && (
                          <span
                            className="sdm-deficit-badge"
                            title={`Fewer than ${IDEAL_RECEPTIONISTS} front desk present on ${fdDeficitDays} day${fdDeficitDays !== 1 ? "s" : ""} this week`}
                          >
                            {fdDeficitDays}d
                          </span>
                        )}
                      </div>
                    </td>
                    {windowDates.map((date) => {
                      const isFuture = date > todayStr;
                      const dayData = dates[date];
                      const hasData = !isFuture && !!dayData;
                      const count = hasData
                        ? (dayData.receptionists || []).filter((s) => s.isPresent).length
                        : 0;
                      const colour = getSubCellColour(count, IDEAL_RECEPTIONISTS, hasData);
                      return (
                        <td key={date}>
                          <span className={`sdm-sub-cell sdm-sub-cell--${colour}`}>
                            {hasData ? count : ""}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MatrixGrid;
